import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAgGridTabbing } from '../../common/datagrid/hooks/useAgGridTabbing';
import '../../common/datagrid/table-style.css';

import { Box } from '@mui/material';
import { CellValueChangedEvent, IRowNode } from 'ag-grid-community';
import { isEqual } from 'lodash';
import { connect, useDispatch } from 'react-redux';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import { RootState } from '../../store';
import { notNull } from '../../utils';
import { AgreementPriceRow } from './agreementPriceTypes';
import {
    getAgreementColumns,
    getAgreementPriceRows,
    getAgreementPriceTable,
    getColumnSettings,
    getSelectedAgreementId
} from './redux/agreementSelectors';
import { saveAgreementPrice, setIsExporting } from './redux/agreementSlices';

const mapStateToProps = (state: RootState) => ({
    columns: getAgreementColumns(state),
    agreementPriceRows: getAgreementPriceRows(state),
    agreementPriceTable: getAgreementPriceTable(state),
    agreementId: getSelectedAgreementId(state),
    columnKeys: getColumnSettings(state),
    isExporting: state.agreement.ui.isExporting
});

type Props = ReturnType<typeof mapStateToProps>;

const AgreementPriceTable = ({ columns, agreementId, agreementPriceRows, isExporting }: Props) => {
    const [rows, setRows] = useState([] as AgreementPriceRow[]);
    const [modifiedRows, setModifiedRows] = useState({} as { [index: number]: AgreementPriceRow });

    const dispatch = useDispatch();

    const gridRef = useRef<AgGridReact>(null);

    useEffect(() => {
        //Refresh isInactive column on data changed.
        const apRows = agreementPriceRows
            .map(agreementPriceRow => {
                const node = gridRef?.current?.api?.getRowNode(agreementPriceRow.productId.toString());
                if (node != null) {
                    return node;
                }
                // gridRef?.current?.api.refreshCells();
                return null;
            })
            .filter(notNull);
        if (apRows.length > 0) {
            gridRef?.current?.api?.refreshCells({
                columns: ['isInactive', 'extPrice'],
                rowNodes: apRows,
                force: true
            });
        }
    }, [agreementPriceRows]);
    useEffect(() => {
        const mRows = {} as { [index: number]: AgreementPriceRow };
        setRows(r => {
            if (r.length === 0) {
                return agreementPriceRows;
            }

            // Extremely important to reuse old rows to prevent all
            // rows from being re-rendered every time a change occurs.
            return agreementPriceRows.map((priceRow, index) => {
                const oldRow = r.find(x => x.productId === priceRow.productId);
                if (oldRow != null && isEqual(oldRow, priceRow)) {
                    return oldRow;
                }
                mRows[index] = priceRow;
                return priceRow;
            });
        });
        setModifiedRows(mRows);
        setupGrid();
    }, [agreementPriceRows]);

    useEffect(() => {
        const rowNodes = [] as IRowNode[];

        for (const index in modifiedRows) {
            const node = gridRef?.current?.api.getDisplayedRowAtIndex(Number(index));
            if (node != null) {
                rowNodes.push(node);
            }
        }
        gridRef?.current?.api?.refreshCells({ rowNodes, force: true });
    }, [modifiedRows]);

    const { tabToNextCell, navigateToNextCell } = useAgGridTabbing(
        gridRef?.current?.columnApi?.getAllDisplayedColumns() ?? [],
        rows,
        gridRef
    );
    useEffect(() => {
        if (isExporting) {
            gridRef?.current?.api?.exportDataAsCsv();
            dispatch(setIsExporting(false));
        }
    }, [isExporting, dispatch]);

    const onRowsChange = useCallback(
        ({ data, colDef }: CellValueChangedEvent<AgreementPriceRow>) => {
            if (
                agreementId != null &&
                (colDef.field === 'price' || colDef.field === 'extPrice' || colDef.field === 'comment')
            ) {
                const priceRow = data;

                const getNumber = (nmbr: number | null | undefined, key: 'price' | 'extPrice') =>
                    colDef.field === key
                        ? nmbr
                            ? Number((nmbr.toString() as unknown as string)?.replace(',', '.'))
                            : 0
                        : nmbr ?? 0;

                const price = getNumber(priceRow.price, 'price');
                const extPrice = getNumber(priceRow.extPrice, 'extPrice');
                if (price === 0 && !priceRow.agreementPriceId) {
                    return;
                }

                if (!isNaN(price) && !isNaN(extPrice)) {
                    dispatch(
                        saveAgreementPrice.request({
                            agreementId: agreementId,
                            agreementPriceId: priceRow.agreementPriceId,
                            productId: priceRow.productId,
                            comment: priceRow.comment,
                            discount: priceRow.discount,
                            externalDiscount: priceRow.extDiscount,
                            price: price,
                            externalPrice: extPrice,
                            type: colDef.field,
                            isActive: priceRow.isActive
                        })
                    );
                }
            }
        },
        [dispatch, agreementId]
    );
    const popupParent = useMemo(() => {
        return document.body;
    }, []);
    return (
        <Box sx={{ height: '75vh' }} /*onKeyDown={handleKeyDown}*/>
            <AgGridReact<AgreementPriceRow>
                className={`ag-theme-alpine`}
                columnDefs={columns}
                rowData={agreementPriceRows}
                headerHeight={25}
                floatingFiltersHeight={30}
                rowHeight={25}
                onFirstDataRendered={setupGrid}
                onCellValueChanged={onRowsChange}
                suppressScrollOnNewData={true}
                getRowId={params => params.data.productId?.toString() ?? ''}
                tabToNextCell={tabToNextCell}
                ref={gridRef}
                navigateToNextCell={navigateToNextCell}
                popupParent={popupParent}
            />
        </Box>
    );
};

export default connect(mapStateToProps)(AgreementPriceTable);
